import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { onboardingQuestionnaireActions } from '../../../../../../../store';
import {
  getAgreementConsentSavedValues,
  getAgreementConsentTouchedState,
  getAgreementDocumentPayload,
  isAgreementConsentFieldName,
} from './utils';

import type { AppDispatch, RootState } from '../../../../../../../store';
import type {
  AgreementConsentSavedValues,
  AgreementFormikProps,
  SaveAgreementConsentOnChange,
  UseSaveAgreementConsentOnChangeDependencies,
  UseSaveAgreementConsentOnChangeResult,
} from './types';
import type { AgreementFormValues } from '../../../../types';

export function useSaveAgreementConsentOnChangeWithDependencies(
  formik: AgreementFormikProps,
  initialValues: AgreementFormValues,
  dependencies: UseSaveAgreementConsentOnChangeDependencies,
): UseSaveAgreementConsentOnChangeResult {
  const { dispatch, getAccessTokenSilently, isUpdating, onboardingQuestionnaireId } = dependencies;
  const formikRef = useRef(formik);
  formikRef.current = formik;
  const savedConsentValuesRef = useRef<AgreementConsentSavedValues>(
    getAgreementConsentSavedValues(initialValues),
  );
  const savingPromiseRef = useRef<Promise<void>>(Promise.resolve());
  const validationPromiseRef = useRef<Promise<void>>(Promise.resolve());

  useEffect(() => {
    savedConsentValuesRef.current = getAgreementConsentSavedValues(initialValues);
  }, [initialValues]);

  const saveConsentOnChange = useCallback<SaveAgreementConsentOnChange>(
    (name, value) => {
      const consentValue = value === true ? true : value === false ? false : undefined;

      if (
        !onboardingQuestionnaireId ||
        !isAgreementConsentFieldName(name) ||
        consentValue === undefined
      ) {
        return;
      }

      const validateConsent = async () => {
        try {
          await validationPromiseRef.current;
        } catch {
          // Swallow prior validation errors so this validation still proceeds.
        }

        const formikSnapshot = formikRef.current;
        const valuesForValidation = { ...formikSnapshot.values, [name]: consentValue };

        await formikSnapshot.setTouched(
          getAgreementConsentTouchedState(valuesForValidation, formikSnapshot.touched),
          false,
        );
        const errors = await formikSnapshot.validateForm(valuesForValidation);
        formikSnapshot.setErrors(errors);
      };

      const validationRun = validateConsent();
      validationPromiseRef.current = validationRun;
      void validationRun;

      if (savedConsentValuesRef.current[name] === consentValue) {
        return;
      }

      const previousSave = savingPromiseRef.current;
      const currentSave = (async () => {
        try {
          await previousSave;
        } catch {
          // Swallow prior save errors so this save still proceeds.
        }

        const valuesForSave = { ...formikRef.current.values, [name]: consentValue };
        const resultAction = await dispatch(
          onboardingQuestionnaireActions.updateOnboardingQuestionnaireAgreement({
            onboardingQuestionnaireId,
            data: getAgreementDocumentPayload(valuesForSave),
            getAccessTokenSilently,
          }),
        );

        if (
          !onboardingQuestionnaireActions.updateOnboardingQuestionnaireAgreement.fulfilled.match(
            resultAction,
          )
        ) {
          return;
        }

        savedConsentValuesRef.current = getAgreementConsentSavedValues(valuesForSave);
      })();

      savingPromiseRef.current = currentSave;
      void currentSave;
    },
    [dispatch, getAccessTokenSilently, onboardingQuestionnaireId],
  );

  return {
    saveConsentOnChange,
    isUpdating,
  };
}

export default function useSaveAgreementConsentOnChange(
  formik: AgreementFormikProps,
  initialValues: AgreementFormValues,
): UseSaveAgreementConsentOnChangeResult {
  const dispatch = useDispatch<AppDispatch>();
  const isUpdating = useSelector(
    (state: RootState) => state.onboardingQuestionnaire.updateAgreementLoading,
  );
  const { getAccessTokenSilently } = useAuth0();
  const { onboardingQuestionnaireId } = useParams<'onboardingQuestionnaireId'>();

  return useSaveAgreementConsentOnChangeWithDependencies(formik, initialValues, {
    dispatch,
    getAccessTokenSilently,
    isUpdating,
    onboardingQuestionnaireId,
  });
}
